<template>
  <span class="syndications-popper__container">
    <slot :toggle-open="handleToggle" />
  </span>
  <Popper
    v-model="isOpen"
    v-bind="popperAttrs"
  >
    <SyndicationsTable
      class="tw:h-full tw:max-h-[50vh] tw:overflow-hidden tw:overflow-y-auto tw:rounded-md tw:bg-white tw:drop-shadow-md tw:max-w-[350px]"
      sort-by="source_name"
      :syndications="syndications"
      @syndication-clicked="syndicationClicked"
    >
      <template #header>
        <th class="tw:w-full tw:px-4 tw:pb-3 tw:pt-3.5 tw:text-left">
          {{ getLocaleText("syndications.source") }}
        </th>
        <th class="tw:px-4">
          <BaseStreemTooltip
            anchor="top middle"
            self="bottom middle"
          >
            {{ getLocaleText("syndications.potential_audience_reach") }}
          </BaseStreemTooltip>
          <BaseIcon
            :aria-label="getLocaleText('syndications.potential_audience_reach')"
            class="tw:text-2xl tw:font-normal"
            icon="followers"
          />
        </th>
        <th
          v-if="canShowAdvertisingValue"
          class="tw:px-4"
        >
          <BaseStreemTooltip
            anchor="top middle"
            self="bottom middle"
          >
            {{ getLocaleText("syndications.advertising_value_equivalency") }}
          </BaseStreemTooltip>

          <AdvertisingValueIcon />
        </th>
      </template>

      <template #body="{ syndicated }">
        <td class="tw:px-4 tw:py-1.5 tw:text-left">
          <BaseButton
            v-if="syndicated.source_name && isOutletEnabled(syndicated.source)"
            class="tw:text-left tw:hover:text-blue-700! tw:hover:underline"
            new-design
            plain
            :title="syndicated.source_name"
            @click.stop.prevent="handleSourceClick(syndicated)"
          >
            {{ syndicated.source_name }}
          </BaseButton>
          <span v-else>
            {{ syndicated.source_name }}
          </span>

          <span
            class="tw:align-center -tw:mt-1 tw:flex tw:items-center tw:text-denim-600"
          >
            <SyndicationsPopperTick
              :is-checked="hasMatchingSyndications(syndicated.id)"
              :is-loading="isLoading"
            />
            <time :datetime="syndicated.timestamp.toString()">
              {{ formatSyndicatedDate(syndicated) }}
            </time>
          </span>
        </td>
        <td>
          <span
            v-if="syndicated.audience"
            class="tw:px-4 tw:block tw:text-xs tw:font-medium tw:max-w-[120px] tw:overflow-hidden tw:text-ellipsis tw:whitespace-nowrap"
          >
            {{ getLocaleNumber(syndicated.audience, "suffix") }}
          </span>

          <BaseIcon
            v-else
            class="tw:text-2xl tw:text-denim-500"
            icon="minus"
          />
        </td>

        <td v-if="canShowAdvertisingValue">
          <span
            v-if="syndicated.advertising_value"
            class="tw:px-4 tw:block tw:text-xs tw:font-medium tw:max-w-[120px] tw:overflow-hidden tw:text-ellipsis tw:whitespace-nowrap"
          >
            {{ getLocaleNumber(syndicated.advertising_value, "suffix") }}
          </span>
          <BaseIcon
            v-else
            class="tw:text-2xl tw:text-denim-500"
            icon="minus"
          />
        </td>
      </template>
    </SyndicationsTable>
  </Popper>
</template>

<script setup lang="ts">
import { computed, inject } from "vue";

import { getLocaleNumber, getLocaleText } from "shared/boot/i18n";
import AdvertisingValueIcon from "shared/components/AdvertisingValueIcon.vue";
import { BaseStreemTooltip } from "shared/components/base";
import BaseButton from "shared/components/base/BaseButton";
import BaseIcon from "shared/components/base/BaseIcon";
import Popper from "shared/components/base/BasePopper";
import useSidebarOnMentionCard from "shared/composables/useSidebarOnMentionCard";
import {
  dateInTimeZone,
  formatDate,
  parseDate,
  shortTimezone,
} from "shared/helpers/date";
import { IsPublicInsightsSymbol } from "shared/modules/Charts/types/symbols";
import features from "shared/services/features";
import type {
  MentionWithSyndication,
  Syndication,
  TvCaptionSyndication,
} from "shared/types/mentions";
import { isTvMention, isTvOrRadioMention } from "shared/types/mentions/guards";

import SyndicationsPopperTick from "./SyndicationsPopperTick.vue";
import SyndicationsTable from "./SyndicationsTable.vue";
import { type SyndicationPopperProps } from "./types";
import { useSyndicationsObserver } from "./useSyndicationsObserver";
import { useSyndicationsPopper } from "./useSyndicationsPopper";

const props = withDefaults(
  defineProps<SyndicationPopperProps<MentionWithSyndication>>(),
  {
    parentElRef: null,
  }
);

const emit = defineEmits<{
  (e: "syndication-clicked", syndication: Syndication): void;
  (e: "source-name-click", source: Syndication["source"]): void;
}>();

const isPublicInsights = inject(IsPublicInsightsSymbol, false);
const { isOutletEnabled } = useSidebarOnMentionCard({});
const { isOpen, toggleOpen, abortController } = useSyndicationsObserver();

const {
  popperAttrs,
  isLoading,
  syndications,
  syndicationsMatchingStream,
  loadSyndicationMatches,
} = useSyndicationsPopper<MentionWithSyndication, Syndication>({
  stream: props.stream,
  mention: props.mention,
  abortController,
});

const handleToggle = () => {
  if (isPublicInsights) {
    return;
  }

  toggleOpen(props.parentElRef, loadSyndicationMatches);
};

const isTv = computed(() => isTvMention(props.mention));

const canShowAdvertisingValue = computed(() =>
  features.has("advertising_value_mentions")
);

const hasMatchingSyndications = computed(
  () => (id: number) => Boolean(syndicationsMatchingStream.value?.[id])
);

const formatSyndicatedDate = (syndicated: Syndication) => {
  const timestamp = isTvOrRadioMention(props.mention)
    ? (syndicated as TvCaptionSyndication).start_time
    : syndicated.published_at;

  if (!syndicated.source) return formatDate(new Date(timestamp), "h:mmaaa");

  const timezone = shortTimezone(
    parseDate(timestamp),
    syndicated.source.time_zone
  );

  const timeString = dateInTimeZone(
    timestamp,
    "h:mmaaa",
    syndicated.source.time_zone
  );

  return `${timeString} ${timezone}`;
};

const syndicationClicked = (syndication: Syndication) => {
  const emitPayload = {
    ...syndication,
    type: props.mention.type,
  };

  if (isTv.value && props.mention.excerpts) {
    emitPayload.excerpts = props.mention.excerpts;
  }

  emit("syndication-clicked", emitPayload);
  isOpen.value = false;
};

const handleSourceClick = (syndication: Syndication) => {
  if (syndication.source) {
    emit("source-name-click", syndication.source);
  }
};

defineExpose({ isOpen, toggleOpen });
</script>
